/**
 * BannerAdmin.jsx — Elite Vacances
 * Gestión de banners con subida al servidor y API dedicada (sin afectar GAC).
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getBannersElite,
  saveBannerElite,
  updateBannerElite,
  deleteBannerElite,
} from "services/bannersElite";
import {
  normalizeBannerElite,
  buildBannerElitePayload,
  getBannerEliteErrorMessage,
} from "utils/bannerElite";
import "../styles/BannerAdmin.css";
import "../styles/admin-theme.css";

// ─── Upload Box ────────────────────────────────────────────────────────
function UploadBox({ label, sizeLabel, preview, onFile }) {
  const ref = useRef();
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };
  return (
    <div
      className="ba-upload-box"
      onClick={() => ref.current.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {preview ? (
        <>
          <div
            className="ba-upload-box__preview"
            style={{ backgroundImage: `url(${preview})` }}
          />
          <span className="ba-upload-box__change">Cambiar</span>
        </>
      ) : (
        <>
          <svg
            className="ba-upload-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span className="ba-upload-label">{label}</span>
          <span className="ba-upload-size">{sizeLabel}</span>
        </>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
      />
    </div>
  );
}

// ─── Formulario (en modal) ─────────────────────────────────────────────
const EMPTY_FORM = { link: "", name: "", order: "", startDate: "", endDate: "" };
const EMPTY_PREV = { mobile: null, tablet: null, pc: null, large: null };
const EMPTY_FILES = { mobile: null, tablet: null, pc: null, large: null };

function BannerForm({
  initial,
  initialPrev,
  onSave,
  onCancel,
  submitLabel = "Crear Banner",
}) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [previews, setPreviews] = useState(initialPrev || EMPTY_PREV);
  const [files, setFiles] = useState(EMPTY_FILES);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (key) => (file) => {
    setFiles((f) => ({ ...f, [key]: file }));
    setPreviews((p) => ({ ...p, [key]: URL.createObjectURL(file) }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre del banner es obligatorio.");
      return;
    }

    if (!form.order || parseInt(form.order, 10) < 1) {
      toast.error("El orden es obligatorio y debe ser un número mayor a 0.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({ form, previews, files });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="ba-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && !isSubmitting && onCancel()}
    >
      <div className="ba-modal">
        <div className="ba-modal__header">
          <h3 className="ba-modal__title">
            {submitLabel === "Crear Banner" ? "🖼 Nuevo Banner" : "✎ Editar Banner"}
          </h3>
          <button
            className="ba-modal__close"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <div className="ba-modal__body">
          <p className="ba-modal__hint">
            Sube las 4 imágenes (móvil, tablet, PC y pantalla grande). Todas son
            obligatorias.
          </p>
          <div className="ba-upload-grid">
            <UploadBox
              label="Móvil"
              sizeLabel="720 × 1280"
              preview={previews.mobile}
              onFile={handleFile("mobile")}
            />
            <UploadBox
              label="Tablet"
              sizeLabel="1280 × 720"
              preview={previews.tablet}
              onFile={handleFile("tablet")}
            />
            <UploadBox
              label="PC"
              sizeLabel="1920 × 1080"
              preview={previews.pc}
              onFile={handleFile("pc")}
            />
            <UploadBox
              label="Pantalla gran"
              sizeLabel="2600 × 800"
              preview={previews.large}
              onFile={handleFile("large")}
            />
          </div>

          <div className="ba-create-fields">
            <div className="ba-field">
              <label>
                Nombre del banner <span className="ba-required">*</span>
              </label>
              <input
                className="ba-input"
                type="text"
                placeholder="Ej: Black Friday Cartagena"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>
            <div className="ba-row2">
              <div className="ba-field">
                <label>Fecha de inicio</label>
                <input
                  className="ba-input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setField("startDate", e.target.value)}
                />
              </div>
              <div className="ba-field">
                <label>Fecha de fin</label>
                <input
                  className="ba-input"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setField("endDate", e.target.value)}
                />
              </div>
            </div>
            <div className="ba-row2">
              <div className="ba-field">
                <label>Link (URL)</label>
                <input
                  className="ba-input"
                  type="url"
                  placeholder="https://…"
                  value={form.link}
                  onChange={(e) => setField("link", e.target.value)}
                />
              </div>
              <div className="ba-field">
                <label>
                  Orden <span className="ba-required">*</span>
                </label>
                <input
                  className="ba-input"
                  type="number"
                  min={1}
                  placeholder="1"
                  value={form.order}
                  onChange={(e) => setField("order", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="ba-modal__footer">
          <button
            className="ba-btn ba-btn--ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            className="ba-btn ba-btn--primary ba-btn--lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Guardando…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tarjeta de banner guardado ────────────────────────────────────────
const RES_LABELS = { mobile: "Móvil", tablet: "Tablet", pc: "PC", large: "Grande" };

function BannerCard({ banner, index, onEdit, onRemove, isRemoving }) {
  const thumb =
    banner.previews.mobile ||
    banner.previews.tablet ||
    banner.previews.pc ||
    banner.previews.large;

  const isActive = () => {
    const now = new Date();
    const start = banner.form.startDate ? new Date(banner.form.startDate) : null;
    const end = banner.form.endDate ? new Date(banner.form.endDate) : null;
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };

  return (
    <div className="ba-slide">
      <div className="ba-slide__header">
        <div className="ba-slide__header-left">
          {thumb && (
            <div
              className="ba-card-thumb"
              style={{ backgroundImage: `url(${thumb})` }}
            />
          )}
          <div>
            <span className="ba-slide__num">
              {banner.form.name || `Banner ${index + 1}`}
            </span>
            <div className="ba-card-meta">
              <span
                className={`ba-badge ${isActive() ? "ba-badge--on" : "ba-badge--off"}`}
              >
                {isActive() ? "● Activo" : "○ Inactivo"}
              </span>
              {banner.form.startDate && (
                <span className="ba-card-dates">
                  {banner.form.startDate} → {banner.form.endDate || "sin fin"}
                </span>
              )}
              {banner.form.order !== "" && banner.form.order != null && (
                <span className="ba-card-dates">Orden: {banner.form.order}</span>
              )}
            </div>
          </div>
        </div>
        <div className="ba-slide__header-actions">
          <button
            className="ba-btn ba-btn--secondary ba-btn--sm"
            onClick={() => onEdit(banner.id)}
            disabled={isRemoving}
          >
            ✎ Editar
          </button>
          <button
            className="ba-remove"
            onClick={() => onRemove(banner.id)}
            disabled={isRemoving}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="ba-card-thumbs">
        {["mobile", "tablet", "pc", "large"].map((k) =>
          banner.previews[k] ? (
            <div key={k} className="ba-card-res">
              <div
                className="ba-card-res__img"
                style={{ backgroundImage: `url(${banner.previews[k]})` }}
              />
              <span className="ba-card-res__lbl">{RES_LABELS[k]}</span>
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
}

// ─── Panel principal ───────────────────────────────────────────────────
export default function BannerAdmin() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const refreshBanners = useCallback(async () => {
    try {
      const { data } = await getBannersElite();
      const sorted = [...data].sort(
        (a, b) => (parseInt(a.order, 10) || 0) - (parseInt(b.order, 10) || 0),
      );
      setBanners(sorted.map(normalizeBannerElite));
    } catch (error) {
      console.error(error);
      toast.error(
        getBannerEliteErrorMessage(error, "No se pudieron cargar los banners"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBanners();
  }, [refreshBanners]);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSaveNew = async ({ form, previews, files }) => {
    try {
      const payload = await buildBannerElitePayload(form, previews, files, {
        isEdit: false,
      });
      await saveBannerElite(payload);
      await refreshBanners();
      setShowForm(false);
      flashSaved();
      toast.success("Banner creado correctamente");
    } catch (error) {
      console.error(error);
      toast.error(
        getBannerEliteErrorMessage(error, "Error al crear el banner"),
      );
    }
  };

  const handleSaveEdit = async ({ form, previews, files }) => {
    try {
      const payload = await buildBannerElitePayload(form, previews, files, {
        isEdit: true,
      });
      await updateBannerElite(editId, payload);
      await refreshBanners();
      setEditId(null);
      flashSaved();
      toast.success("Banner actualizado correctamente");
    } catch (error) {
      console.error(error);
      toast.error(
        getBannerEliteErrorMessage(error, "Error al actualizar el banner"),
      );
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("¿Eliminar este banner?")) return;

    setRemovingId(id);
    try {
      await deleteBannerElite(id);
      await refreshBanners();
      toast.success("Banner eliminado");
    } catch (error) {
      console.error(error);
      toast.error(
        getBannerEliteErrorMessage(error, "No se pudo eliminar el banner"),
      );
    } finally {
      setRemovingId(null);
    }
  };

  const editing = banners.find((b) => b.id === editId) || null;

  return (
    <div className="ba-wrap">
      <div className="ba-shell">
        <header className="ba-topbar">
          <div className="ba-topbar__left">
            <button
              className="ba-back"
              onClick={() => navigate("/8c4f9d17b6e245dca9814f7a5b6d3e2f")}
            >
              ← Volver
            </button>
            <div>
              <h1 className="ba-title">
                <span className="ba-logo">▶</span>
                Banners
                <span className="ba-version">v4</span>
              </h1>
              <p className="ba-topbar__sub">Elite Vacances · Gestión de banners</p>
            </div>
          </div>
          <button
            className="ba-btn ba-btn--primary ba-btn--new"
            onClick={() => setShowForm(true)}
          >
            ＋ Nuevo Banner
          </button>
        </header>

        <div className="ba-body">
      {saved && (
        <div className="ba-notice ba-notice--ok">
          ✓ Cambios guardados correctamente.
        </div>
      )}

      {loading ? (
        <div className="ba-empty">
          <p className="ba-empty__text">Cargando banners…</p>
        </div>
      ) : banners.length === 0 ? (
        <div className="ba-empty">
          <svg viewBox="0 0 64 48" fill="none" width="72" height="54">
            <rect x="2" y="4" width="60" height="38" rx="5" fill="#e4ddd0" />
            <rect x="8" y="10" width="48" height="26" rx="3" fill="#f8f6f0" />
            <line
              x1="24"
              y1="23"
              x2="40"
              y2="23"
              stroke="#c8bfa8"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="28"
              y1="29"
              x2="36"
              y2="29"
              stroke="#c8bfa8"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <p className="ba-empty__text">Aún no hay banners creados.</p>
          <button
            className="ba-btn ba-btn--primary"
            onClick={() => setShowForm(true)}
          >
            ＋ Crear primer banner
          </button>
        </div>
      ) : (
        <div className="ba-list">
          {banners.map((b, i) => (
            <BannerCard
              key={b.id || i}
              banner={b}
              index={i}
              onEdit={setEditId}
              onRemove={handleRemove}
              isRemoving={removingId === b.id}
            />
          ))}
        </div>
      )}

      {showForm && (
        <BannerForm
          onSave={handleSaveNew}
          onCancel={() => setShowForm(false)}
          submitLabel="Crear Banner"
        />
      )}

      {editing && (
        <BannerForm
          key={editId}
          initial={editing.form}
          initialPrev={editing.previews}
          onSave={handleSaveEdit}
          onCancel={() => setEditId(null)}
          submitLabel="Guardar cambios"
        />
      )}
        </div>
      </div>
    </div>
  );
}
